// Gestão de Contas (Admin)
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, Building, AlertCircle, X, Link as LinkIcon } from 'lucide-react';
import { getContas, getContaById } from '../../../services/contasService';
import { getEmpresas } from '../../../services/empresasService';
import { supabase } from '../../../lib/supabase';
import type { Conta } from '../../../services/contasService';
import type { Empresa } from '../../../types';

export function Contas() {
  const [busca, setBusca] = useState('');
  const [contas, setContas] = useState<Conta[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresasConta, setEmpresasConta] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCriarModalOpen, setIsCriarModalOpen] = useState(false);
  const [contaEditando, setContaEditando] = useState<Conta | null>(null);
  const [empresaParaVincular, setEmpresaParaVincular] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    email: '',
    telefone: '',
  });

  useEffect(() => {
    carregarContas();
    carregarEmpresas();
  }, []);

  const carregarContas = async () => {
    try {
      setLoading(true);
      setError(null);
      const contasData = await getContas();
      setContas(contasData);
    } catch (err: any) {
      console.error('[Contas] Erro ao carregar contas:', err);
      setError(err.message || 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  };

  const carregarEmpresas = async () => {
    try {
      const empresasData = await getEmpresas();
      setEmpresas(empresasData);
    } catch (error) {
      console.error('[Contas] Erro ao carregar empresas:', error);
    }
  };

  const carregarEmpresasConta = async (contaId: string) => {
    try {
      const empresasData = await getEmpresas();
      const empresasFiltradas = empresasData.filter(e => (e as any).contaId === contaId);
      setEmpresasConta(empresasFiltradas);
    } catch (err: any) {
      console.error('[Contas] Erro ao carregar empresas da conta:', err);
      setEmpresasConta([]);
    }
  };

  const handleCriarConta = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const { data, error: insertError } = await (supabase as any)
        .from('contas')
        .insert({
          nome: formData.nome,
          cnpj: formData.cnpj || null,
          email: formData.email || null,
          telefone: formData.telefone || null,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Limpar formulário
      setFormData({
        nome: '',
        cnpj: '',
        email: '',
        telefone: '',
      });

      setIsCriarModalOpen(false);
      await carregarContas();
    } catch (err: any) {
      console.error('[Contas] Erro ao criar conta:', err);
      setError(err.message || 'Erro ao criar conta');
    } finally {
      setIsSaving(false);
    }
  };

  const handleVincularEmpresa = async () => {
    if (!contaEditando || !empresaParaVincular) {
      setError('Selecione uma empresa para vincular');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Atualizar conta_id da empresa
      const { error: updateError } = await (supabase as any)
        .from('empresas')
        .update({ conta_id: contaEditando.id })
        .eq('id', empresaParaVincular);

      if (updateError) throw updateError;

      setEmpresaParaVincular('');
      await carregarEmpresasConta(contaEditando.id);
      await carregarEmpresas();
    } catch (err: any) {
      console.error('[Contas] Erro ao vincular empresa:', err);
      setError(err.message || 'Erro ao vincular empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDesvincularEmpresa = async (empresaId: string) => {
    if (!contaEditando) return;

    if (!confirm('Tem certeza que deseja desvincular esta empresa da conta?')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Remover conta_id da empresa (setar null)
      const { error: updateError } = await (supabase as any)
        .from('empresas')
        .update({ conta_id: null })
        .eq('id', empresaId);

      if (updateError) throw updateError;

      await carregarEmpresasConta(contaEditando.id);
      await carregarEmpresas();
    } catch (err: any) {
      console.error('[Contas] Erro ao desvincular empresa:', err);
      setError(err.message || 'Erro ao desvincular empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExcluirConta = async (contaId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta conta? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Verificar se há empresas vinculadas
      const empresasVinculadas = empresas.filter(e => (e as any).contaId === contaId);
      if (empresasVinculadas.length > 0) {
        throw new Error(`Não é possível excluir a conta. Existem ${empresasVinculadas.length} empresa(s) vinculada(s). Desvincule as empresas primeiro.`);
      }

      const { error: deleteError } = await (supabase as any)
        .from('contas')
        .delete()
        .eq('id', contaId);

      if (deleteError) throw deleteError;

      await carregarContas();
      if (contaEditando?.id === contaId) {
        setIsModalOpen(false);
        setContaEditando(null);
      }
    } catch (err: any) {
      console.error('[Contas] Erro ao excluir conta:', err);
      setError(err.message || 'Erro ao excluir conta');
    } finally {
      setIsSaving(false);
    }
  };

  const abrirModal = async (conta?: Conta) => {
    setContaEditando(conta || null);
    setIsModalOpen(true);
    
    if (conta) {
      await carregarEmpresasConta(conta.id);
    } else {
      setEmpresasConta([]);
    }
    
    setEmpresaParaVincular('');
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setContaEditando(null);
    setEmpresasConta([]);
    setEmpresaParaVincular('');
  };

  const contasFiltradas = contas.filter((c) =>
    c.nome.toLowerCase().includes(busca.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(busca.toLowerCase())) ||
    (c.cnpj && c.cnpj.includes(busca))
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Contas' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Contas' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestão de Contas</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerenciar contas e vincular empresas</p>
        </div>
        <Button variant="primary" onClick={() => setIsCriarModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Busca */}
      <Card>
        <Input
          label="Buscar Conta"
          placeholder="Nome, CNPJ ou email"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>

      {/* Tabela */}
      <Card>
        {contasFiltradas.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">Nenhuma conta encontrada</p>
          </div>
        ) : (
          <Table
            data={contasFiltradas}
            columns={[
              {
                key: 'nome',
                label: 'Nome',
                render: (item: Conta) => (
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{item.nome}</span>
                  </div>
                ),
              },
              {
                key: 'cnpj',
                label: 'CNPJ',
                render: (item: Conta) => item.cnpj || '-',
              },
              {
                key: 'email',
                label: 'Email',
                render: (item: Conta) => item.email || '-',
              },
              {
                key: 'telefone',
                label: 'Telefone',
                render: (item: Conta) => item.telefone || '-',
              },
              {
                key: 'actions',
                label: 'Ações',
                render: (item: Conta) => (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => abrirModal(item)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                      title="Ver detalhes e gerenciar empresas"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleExcluirConta(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Excluir conta"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Modal de Criação */}
      <Modal
        isOpen={isCriarModalOpen}
        onClose={() => setIsCriarModalOpen(false)}
        title="Nova Conta"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            required
            placeholder="Nome da conta"
          />
          <Input
            label="CNPJ"
            value={formData.cnpj}
            onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
            placeholder="CNPJ (opcional)"
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Email (opcional)"
          />
          <Input
            label="Telefone"
            value={formData.telefone}
            onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            placeholder="Telefone (opcional)"
          />

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={() => setIsCriarModalOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleCriarConta} disabled={!formData.nome || isSaving}>
              {isSaving ? 'Salvando...' : 'Criar Conta'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Edição */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={contaEditando ? 'Detalhes da Conta' : 'Nova Conta'}
        size="lg"
      >
        <div className="space-y-4">
          {contaEditando && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome
                </label>
                <p className="text-gray-900 dark:text-gray-100">{contaEditando.nome}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CNPJ
                </label>
                <p className="text-gray-900 dark:text-gray-100">{contaEditando.cnpj || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <p className="text-gray-900 dark:text-gray-100">{contaEditando.email || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefone
                </label>
                <p className="text-gray-900 dark:text-gray-100">{contaEditando.telefone || '-'}</p>
              </div>

              {/* Seção de Empresas Vinculadas */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Empresas Vinculadas ({empresasConta.length})
                </label>
                {empresasConta.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {empresasConta.map((empresa) => (
                      <div key={empresa.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {empresa.nomeFantasia}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {empresa.cnpj} • {empresa.email}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDesvincularEmpresa(empresa.id)}
                          disabled={isSaving}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Desvincular empresa"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Nenhuma empresa vinculada</p>
                )}

                {/* Seção para vincular nova empresa */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Vincular Nova Empresa
                  </label>
                  <div className="space-y-3">
                    <Select
                      label="Empresa"
                      value={empresaParaVincular}
                      onChange={(e) => setEmpresaParaVincular(e.target.value)}
                      options={[
                        { value: '', label: 'Selecione uma empresa' },
                        ...empresas
                          .filter(e => !empresasConta.find(ec => ec.id === e.id))
                          .map((e) => ({
                            value: e.id,
                            label: `${e.nomeFantasia} (${e.cnpj})`,
                          })),
                      ]}
                    />
                    <Button
                      variant="primary"
                      onClick={handleVincularEmpresa}
                      disabled={!empresaParaVincular || isSaving}
                      className="w-full"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Vincular Empresa
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={fecharModal}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

